import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  dateToTaipeiHour,
  formatTaipeiHour,
  nextTaipeiHour,
  taipeiHourToDate,
  validatePublishAt,
} from "@/utils/publishSchedule";

interface PublishScheduleDialogProps {
  isOpen: boolean;
  current: Date | null;
  pending: boolean;
  error: string | null;
  onSave: (at: Date) => void;
  onCancelSchedule: () => void;
  onClose: () => void;
}

const HOURS = Array.from({ length: 24 }, (_, h) => h);

export default function PublishScheduleDialog({
  isOpen,
  current,
  pending,
  error,
  onSave,
  onCancelSchedule,
  onClose,
}: PublishScheduleDialogProps) {
  const [date, setDate] = useState("");
  const [hour, setHour] = useState(20);

  useEffect(() => {
    if (!isOpen) return;
    const initial = dateToTaipeiHour(current ?? nextTaipeiHour());
    setDate(initial.date);
    setHour(initial.hour);
  }, [isOpen, current]);

  const at = taipeiHourToDate(date, hour);
  const invalid = validatePublishAt(at);
  const minDate = dateToTaipeiHour(nextTaipeiHour()).date;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !pending && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{current ? "修改預約上架" : "預約上架"}</DialogTitle>
          <DialogDescription>
            到點後系統自動上架（台灣時間整點）。首頁與列表約 5 分鐘內陸續出現，與手動上架相同。
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-[1fr_auto] gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="publish-date">日期</Label>
            <Input
              id="publish-date"
              type="date"
              min={minDate}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="publish-hour">時間</Label>
            <select
              id="publish-hour"
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={hour}
              onChange={(e) => setHour(Number(e.target.value))}
            >
              {HOURS.map((h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, "0")}:00
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="text-sm">
          {invalid ? (
            <span className="text-destructive">{invalid}</span>
          ) : (
            <span className="text-muted-foreground">
              將於 <span className="font-medium text-foreground">{formatTaipeiHour(at!)}</span>（台灣時間）自動上架
            </span>
          )}
        </div>
        {error && <div className="text-sm text-destructive">{error}</div>}

        <DialogFooter className="gap-2 sm:justify-between">
          {current ? (
            <Button type="button" variant="outline" onClick={onCancelSchedule} disabled={pending}>
              取消預約
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
              關閉
            </Button>
            <Button type="button" onClick={() => at && onSave(at)} disabled={pending || !!invalid}>
              {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              儲存預約
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
